/**
 * ENG-8574: read the session archive address this host publishes.
 *
 * WHY THIS IS A READER AND NOT A RESOLVER. Every tool-call audit row records
 * where its evidence transcript lives, and ENG-8428 serves that S3 object as the
 * authoritative evidence behind the row. Resolving the address here would mean a
 * SECOND implementation of three things the archiver already does — the SSM
 * bucket lookup, the IMDS instance-ARN derivation, and the conditional org
 * segment in `key_prefix="${ORG_ID:+${ORG_ID}/}${instance_arn}"` — each of which
 * has already been got wrong once in this codebase's history (the
 * `ip-10-10-11-212` key still sitting in the prod bucket came from a host whose
 * IMDS lookup failed and which then guessed an identity from its hostname).
 *
 * Two producers of one address drift, and the drift does not surface as an
 * error: it surfaces as an evidence pane that opens onto nothing, which reads to
 * the person relying on it as "the agent did nothing". That is the exact
 * ambiguity the audit log exists to remove, so the address has exactly one
 * resolver — `scripts/agt-session-archiver.sh`, which computes it anyway — and
 * this module only copies what that wrote.
 *
 * The file is written on every archiver run (daily timer), before any upload is
 * attempted, and lives under /var/lib rather than /run so a reboot does not
 * blank it until the next timer.
 */

import { readFileSync } from 'node:fs';

import type { HostArchiveAddress } from './tool-call-extractor.js';

/** Must match `ADDRESS_FILE` in scripts/agt-session-archiver.sh. */
export const DEFAULT_ARCHIVE_ADDRESS_PATH = '/var/lib/augmented/session-archive-address.json';

/**
 * Re-read at most this often. The address is a property of the host and does not
 * change between archiver runs, but the manager consults it on a per-agent tick,
 * so an unbounded re-read would stat the same file thousands of times an hour.
 * Short enough that a freshly-provisioned host picks up its first published
 * address within minutes of the archiver's first run.
 */
const CACHE_TTL_MS = 5 * 60 * 1000;

// Keyed by the RESOLVED path, not global. A single-slot cache returns file A's
// address to a caller that asked for file B — silently attaching the wrong
// archive location to every row for the rest of the TTL. (CodeRabbit.)
const cache = new Map<string, { at: number; value: HostArchiveAddress | null }>();

/** Test seam, mirroring `_resetMemoryExtractorState`. */
export function _resetHostArchiveAddressCache(): void {
  cache.clear();
}

/** '' and non-strings become null: absent and empty mean the same thing here. */
function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Read the published address, or null when this host has none.
 *
 * Null is returned — rather than a partially-populated address — whenever either
 * half of the object's location is missing. `bucket` and `keyPrefix` together
 * ARE the address; with one of them absent a row would carry an address-shaped
 * value that can never resolve to an object, which is strictly worse than a null
 * the retrieval route already renders as an explicit "unavailable" state. An
 * address that cannot locate anything must not look like one.
 */
export function readHostArchiveAddress(path?: string): HostArchiveAddress | null {
  // ARCHIVE_ADDRESS_FILE is the name the archiver itself honours
  // (agt-session-archiver.sh). Reading only the AGT_-prefixed alias meant an
  // operator who redirected the archiver left the manager reading the default
  // path — finding nothing, and shipping rows with no evidence pointer. The
  // archiver's own name wins; the alias is kept for anything already setting it.
  // (CodeRabbit.)
  const file =
    path ??
    process.env['ARCHIVE_ADDRESS_FILE']?.trim() ??
    process.env['AGT_SESSION_ARCHIVE_ADDRESS_FILE']?.trim() ??
    DEFAULT_ARCHIVE_ADDRESS_PATH;

  const now = Date.now();
  const hit = cache.get(file);
  if (hit && now - hit.at < CACHE_TTL_MS) return hit.value;

  let value: HostArchiveAddress | null = null;
  try {
    const raw = readFileSync(file, 'utf-8');
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      const bucket = str(obj['bucket']);
      const keyPrefix = str(obj['key_prefix']);
      // Both or nothing — see the doc comment.
      if (bucket && keyPrefix) {
        value = {
          bucket,
          region: str(obj['region']),
          instanceId: str(obj['instance_id']),
          keyPrefix,
        };
      }
    }
  } catch {
    // Absent (archiver never ran, or this host does not archive), unreadable, or
    // a torn write caught mid-rename. All three mean "address unknown", which is
    // a truthful answer and never an error worth failing a scan over.
    value = null;
  }

  cache.set(file, { at: now, value });
  return value;
}
